import { useEffect, useRef, useState } from 'react';

import {
  FiMap,
  FiUsers,
  FiRadio,
  FiLayers,
  FiMaximize2,
  FiX
} from 'react-icons/fi';

import MapView from './MapView';
import TerritorialPanel from './TerritorialPanel';

function MapSection({
  projectActors = [],
  projectAlerts = [],
  projectSignals = [],
  projectTimeline = [],
  mode = 'view',
  selectedCoordinates,
  selectedEntity,
  onMapClick,
  territorialFilters,
  setTerritorialFilters,
  activeProject,
  pdfMode = false
}) {
  const [showActors, setShowActors] = useState(true);
  const [showSignals, setShowSignals] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowActors(false);

      setTimeout(() => {
        setShowActors(true);
      }, 80);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const [showTagSelector, setShowTagSelector] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  const [localFilters, setLocalFilters] = useState({
    selectedRegion: '',
    selectedComuna: '',
    selectedSector: '',
    selectedFoco: ''
  });

  const activeFilters = territorialFilters || localFilters;
  const updateFilters = setTerritorialFilters || setLocalFilters;

  const {
    selectedRegion = '',
    selectedComuna = '',
    selectedSector = '',
    selectedFoco = ''
  } = activeFilters;

  const [selectedItem, setSelectedItem] = useState(null);
  const [activeMapItem, setActiveMapItem] = useState(null);
  const [ignoreExternalSelection, setIgnoreExternalSelection] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showRelations, setShowRelations] = useState(true);

  const tagSelectorRef = useRef(null);
  const lastExternalIdRef = useRef(null);

  useEffect(() => {
    const currentExternalId = selectedEntity?.id || null;

    if (currentExternalId !== lastExternalIdRef.current) {
      lastExternalIdRef.current = currentExternalId;
      setIgnoreExternalSelection(false);
      setShowRelations(false);
    }
  }, [selectedEntity]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        tagSelectorRef.current &&
        !tagSelectorRef.current.contains(event.target)
      ) {
        setShowTagSelector(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const allItems = [...projectActors, ...projectAlerts, ...projectSignals];

  const regionesDisponibles = getUniqueValues(allItems, 'region');

  const comunasDisponibles = getUniqueValues(
    allItems.filter((item) =>
      selectedRegion ? item.region === selectedRegion : true
    ),
    'comuna'
  );

  const sectoresDisponibles = getUniqueValues(
    allItems.filter((item) =>
      selectedComuna ? item.comuna === selectedComuna : true
    ),
    'sector'
  );

  const focosDisponibles = getUniqueValues(allItems, 'foco');

  const allTags = Array.from(
    new Set(
      allItems
        .flatMap((item) =>
          String(item.tags || '')
            .split(',')
            .map((tag) => tag.trim().toLowerCase())
        )
        .filter(Boolean)
    )
  ).sort();

  const filteredActors = projectActors.filter((actor) => {
    if (!showActors) return false;

    return (
      itemMatchesSelectedTags(actor, selectedTags) &&
      itemMatchesTerritorialFilters(actor, {
        selectedRegion,
        selectedComuna,
        selectedSector,
        selectedFoco
      })
    );
  });

  const filteredAlerts = [];

  const filteredSignals = projectSignals.filter((signal) => {
    if (!showSignals) return false;

    return (
      itemMatchesSelectedTags(signal, selectedTags) &&
      itemMatchesTerritorialFilters(signal, {
        selectedRegion,
        selectedComuna,
        selectedSector,
        selectedFoco
      })
    );
  });

  const activeEntity =
    activeMapItem || (!ignoreExternalSelection ? selectedEntity : null);

  const totalPoints =
    filteredActors.length + filteredAlerts.length + filteredSignals.length;

  const activeTerritorialFilters =
    selectedRegion || selectedComuna || selectedSector || selectedFoco;

  const clearConnections = () => {
    setSelectedItem(null);
    setActiveMapItem(null);
    setIgnoreExternalSelection(true);
    setShowRelations(false);
  };

  const clearTerritorialFilters = () => {
    updateFilters({
      selectedRegion: '',
      selectedComuna: '',
      selectedSector: '',
      selectedFoco: ''
    });
  };

  return (
    <div style={isFullscreen ? styles.fullscreenOverlay : undefined}>
      <div
        style={{
          ...styles.container,
          ...(pdfMode ? styles.containerPdf : {}),
          ...(isFullscreen ? styles.containerFullscreen : {})
        }}
      >
        {!pdfMode && (
          <>
            <div style={styles.header}>
              <div>
                <div style={styles.badge}>
                  <FiMap />
                  INTELLIGENCE LAYER
                </div>

                <h2 style={styles.title}>Mapa territorial</h2>

                <p style={styles.subtitle}>
                  Lectura georreferenciada de actores, alertas y señales sociales.
                </p>
              </div>

              <div style={styles.headerActions}>
                <div style={styles.statusBox}>
                  <FiLayers />
                  <strong>{totalPoints}</strong>
                  <span>elementos visibles</span>
                </div>

                <button
                  style={styles.fullscreenButton}
                  onClick={() => setIsFullscreen(!isFullscreen)}
                >
                  {isFullscreen ? <FiX /> : <FiMaximize2 />}
                  {isFullscreen ? 'Salir' : 'Pantalla completa'}
                </button>
              </div>
            </div>

            <div style={styles.intelligenceBar}>
              <div style={styles.controlsGroup}>
                <ToggleButton
                  active={showActors}
                  icon={<FiUsers />}
                  label="Stakeholders"
                  count={projectActors.length}
                  onClick={() => setShowActors(!showActors)}
                />
                
                <ToggleButton
                  active={showSignals}
                  icon={<FiRadio />}
                  label="RRSS"
                  count={projectSignals.length}
                  onClick={() => setShowSignals(!showSignals)}
                  success
                />
              </div>

              <div ref={tagSelectorRef} style={styles.tagSelectorWrapper}>
                <button
                  style={styles.tagSelectorButton}
                  onClick={() => setShowTagSelector(!showTagSelector)}
                >
                  Seleccionar etiquetas ({selectedTags.length})
                </button>

                {selectedTags.length > 0 && (
                  <button
                    style={styles.clearTagsButton}
                    onClick={() => setSelectedTags([])}
                  >
                    Limpiar
                  </button>
                )}

                {showTagSelector && (
                  <div style={styles.tagDropdown}>
                    {allTags.length === 0 && (
                      <div style={styles.emptyTags}>
                        No existen etiquetas en este proyecto
                      </div>
                    )}

                    {allTags.map((tag) => {
                      const active = selectedTags.includes(tag);

                      return (
                        <button
                          key={tag}
                          style={{
                            ...styles.tagOption,
                            background: active
                              ? 'rgba(6,182,212,0.16)'
                              : 'transparent',
                            border: active
                              ? '1px solid rgba(103,232,249,0.35)'
                              : '1px solid transparent',
                            color: active
                              ? '#dffafe'
                              : 'rgba(255,255,255,0.68)'
                          }}
                          onClick={() => {
                            if (active) {
                              setSelectedTags(
                                selectedTags.filter((t) => t !== tag)
                              );
                            } else {
                              setSelectedTags([...selectedTags, tag]);
                            }
                          }}
                        >
                          #{tag}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div style={styles.territorialFilters}>
              <select
                style={styles.filterSelect}
                value={selectedRegion}
                onChange={(e) => {
                  updateFilters({
                    ...activeFilters,
                    selectedRegion: e.target.value,
                    selectedComuna: '',
                    selectedSector: ''
                  });
                }}
              >
                <option style={styles.option} value="">
                  Todas las regiones
                </option>

                {regionesDisponibles.map((region) => (
                  <option style={styles.option} key={region} value={region}>
                    {region}
                  </option>
                ))}
              </select>

              <select
                style={styles.filterSelect}
                value={selectedComuna}
                onChange={(e) => {
                  updateFilters({
                    ...activeFilters,
                    selectedComuna: e.target.value,
                    selectedSector: ''
                  });
                }}
              >
                <option style={styles.option} value="">
                  Todas las comunas
                </option>

                {comunasDisponibles.map((comuna) => (
                  <option style={styles.option} key={comuna} value={comuna}>
                    {comuna}
                  </option>
                ))}
              </select>

              <select
                style={styles.filterSelect}
                value={selectedSector}
                onChange={(e) => {
                  updateFilters({
                    ...activeFilters,
                    selectedSector: e.target.value
                  });
                }}
              >
                <option style={styles.option} value="">
                  Todos los sectores
                </option>

                {sectoresDisponibles.map((sector) => (
                  <option style={styles.option} key={sector} value={sector}>
                    {sector}
                  </option>
                ))}
              </select>

              <select
                style={styles.filterSelect}
                value={selectedFoco}
                onChange={(e) => {
                  updateFilters({
                    ...activeFilters,
                    selectedFoco: e.target.value
                  });
                }}
              >
                <option style={styles.option} value="">
                  Todos los focos
                </option>

                {focosDisponibles.map((foco) => (
                  <option style={styles.option} key={foco} value={foco}>
                    {foco}
                  </option>
                ))}
              </select>

              {activeTerritorialFilters && (
                <button
                  style={styles.clearTerritoryButton}
                  onClick={clearTerritorialFilters}
                >
                  Limpiar territorio
                </button>
              )}
            </div>

            {activeEntity && (
              <div style={styles.relationsBar}>
                <button
                  style={{
                    ...styles.relationsButton,
                    background: showRelations
                      ? 'linear-gradient(135deg,#0891b2,#0f766e)'
                      : 'rgba(255,255,255,0.05)'
                  }}
                  onClick={() => setShowRelations((current) => !current)}
                >
                  {showRelations ? 'Ocultar relaciones' : 'Mostrar relaciones'}
                </button>
              </div>
            )}

            {activeEntity && (
              <div style={styles.activeEntity}>
                <span>
                  Entidad activa:{' '}
                  <strong>
                    {activeEntity.nombre ||
                      activeEntity.titulo ||
                      activeEntity.red ||
                      'Registro territorial'}
                  </strong>
                </span>

                <button
                  style={styles.clearConnectionsButton}
                  onClick={clearConnections}
                >
                  Limpiar conexiones
                </button>
              </div>
            )}

            {mode === 'select' && (
              <div style={styles.selectionHint}>
                Haz clic en el mapa para seleccionar coordenadas territoriales.
              </div>
            )}
          </>
        )}

        <div
          style={{
            ...styles.mapShell,
            ...(pdfMode ? styles.mapShellPdf : {}),
            ...(isFullscreen ? styles.mapShellFullscreen : {})
          }}
        >
          <MapView
            key={isFullscreen ? 'map-fullscreen' : 'map-normal'}
            projectActors={filteredActors}
            projectAlerts={filteredAlerts}
            projectSignals={filteredSignals}
            mode={mode}
            pdfMode={pdfMode}
            activeProject={activeProject}
            selectedCoordinates={selectedCoordinates}
            selectedEntity={activeEntity}
            showRelations={showRelations}
            onMapClick={onMapClick}
            onTerritorialSelect={(item) => {
              setSelectedItem(item);
              setActiveMapItem(item);
              setIgnoreExternalSelection(false);
              setShowRelations(true);
            }}
          />
        </div>

        {!pdfMode && (
          <>
            <div style={styles.legend}>
              <div style={styles.legendItem}>
                <span style={{ ...styles.dot, background: '#3b82f6' }} />
                Stakeholders
              </div>

              <div style={styles.legendItem}>
                <span style={{ ...styles.dot, background: '#d97706' }} />
                Alertas territoriales
              </div>

              <div style={styles.legendItem}>
                <span style={{ ...styles.dot, background: '#06b6d4' }} />
                Señales RRSS
              </div>

              {showRelations && (
                <>
                  <div style={styles.legendSeparator} />

                  <div style={styles.legendItem}>
                    <span style={{ ...styles.lineRel, borderColor: '#ef4444' }} />
                    Convergencia
                  </div>

                  <div style={styles.legendItem}>
                    <span style={{ ...styles.lineRel, borderColor: '#22c55e' }} />
                    Aliado
                  </div>

                  <div style={styles.legendItem}>
                    <span style={{ ...styles.lineRelDashed, borderColor: '#f97316' }} />
                    Tensión
                  </div>

                  <div style={styles.legendItem}>
                    <span style={{ ...styles.lineRel, borderColor: '#67e8f9' }} />
                    Coordinación
                  </div>
                </>
              )}

              {isFullscreen && (
                <div style={styles.fullscreenHint}>
                  Presiona ESC para salir de pantalla completa.
                </div>
              )}

              {selectedCoordinates?.latitud && selectedCoordinates?.longitud && (
                <div style={styles.coordinates}>
                  Coordenadas: {selectedCoordinates.latitud},{' '}
                  {selectedCoordinates.longitud}
                </div>
              )}
            </div>

            <TerritorialPanel
              selectedItem={selectedItem}
              onClose={() => setSelectedItem(null)}
              projectActors={projectActors}
              projectAlerts={projectAlerts}
              projectSignals={projectSignals}
              projectTimeline={projectTimeline}
              onRelationsModeChange={setShowRelations}
              onRelatedSelect={(item) => {
                setSelectedItem(item);
                setActiveMapItem(item);
                setIgnoreExternalSelection(false);
                setShowRelations(true);
              }}
              closeDelay={800}
            />
          </>
        )}
      </div>
    </div>
  );
}

function getUniqueValues(items, field) {
  return Array.from(
    new Set(
      items
        .map((item) => String(item[field] || '').trim())
        .filter(Boolean)
    )
  ).sort();
}

function itemMatchesSelectedTags(item, selectedTags) {
  if (selectedTags.length === 0) return true;

  const itemTags = String(item.tags || '')
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean);

  return selectedTags.some((tag) => itemTags.includes(tag));
}

function itemMatchesTerritorialFilters(
  item,
  {
    selectedRegion,
    selectedComuna,
    selectedSector,
    selectedFoco
  }
) {
  if (selectedRegion && item.region !== selectedRegion) return false;
  if (selectedComuna && item.comuna !== selectedComuna) return false;
  if (selectedSector && item.sector !== selectedSector) return false;
  if (selectedFoco && item.foco !== selectedFoco) return false;

  return true;
}

function ToggleButton({
  active,
  icon,
  label,
  count,
  onClick,
  warning,
  success
}) {
  let activeBackground = 'linear-gradient(135deg,#1d4ed8,#0891b2)';
  let glow = '0 0 18px rgba(8,145,178,0.35)';
  let countColor = '#7dd3fc';

  if (warning) {
    activeBackground = 'linear-gradient(135deg,#b45309,#d97706)';
    glow = '0 0 18px rgba(217,119,6,0.35)';
    countColor = '#fde68a';
  }

  if (success) {
    activeBackground = 'linear-gradient(135deg,#0e7490,#06b6d4)';
    glow = '0 0 18px rgba(6,182,212,0.35)';
    countColor = '#a5f3fc';
  }

  return (
    <button
      style={{
        ...styles.toggle,
        background: active ? activeBackground : 'rgba(255,255,255,0.045)',
        color: active ? 'white' : 'rgba(255,255,255,0.45)',
        border: active
          ? '1px solid rgba(255,255,255,0.18)'
          : '1px solid rgba(255,255,255,0.06)',
        boxShadow: active ? glow : 'none',
        opacity: active ? 1 : 0.68,
        transform: active ? 'translateY(-1px)' : 'none'
      }}
      onClick={onClick}
    >
      <span style={styles.toggleIcon}>{icon}</span>
      <span>{label}</span>
      <strong style={{ color: active ? 'white' : countColor }}>{count}</strong>
    </button>
  );
}

const styles = {
  fullscreenOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9000,
    background: 'rgba(2,8,23,0.96)',
    padding: '18px',
    overflow: 'auto'
  },

  container: {
    background:
      'radial-gradient(circle at top left,rgba(31,169,122,0.10),transparent 26%), radial-gradient(circle at top right,rgba(9,147,181,0.10),transparent 30%), linear-gradient(135deg,#071a2f 0%,#0b2442 48%,#04101d 100%)',
    borderRadius: '26px',
    padding: '24px',
    boxShadow: '0 20px 45px rgba(0,0,0,0.22)',
    border: '1px solid rgba(255,255,255,0.07)',
    marginBottom: '34px'
  },

  containerPdf: {
    padding: '0',
    borderRadius: '18px',
    marginBottom: '0',
    boxShadow: 'none',
    border: 'none',
    background: '#071a2f'
  },

  containerFullscreen: {
    minHeight: 'calc(100vh - 36px)',
    marginBottom: 0,
    borderRadius: '24px',
    boxShadow: '0 30px 80px rgba(0,0,0,0.55)'
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '20px',
    marginBottom: '18px'
  },

  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
  },

  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    background: 'rgba(255,255,255,0.05)',
    color: 'rgba(32,214,181,0.82)',
    padding: '7px 13px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1px',
    marginBottom: '14px',
    border: '1px solid rgba(255,255,255,0.08)'
  },

  title: {
    margin: 0,
    color: 'white',
    fontSize: '28px',
    letterSpacing: '-0.4px'
  },

  subtitle: {
    color: 'rgba(255,255,255,0.62)',
    marginTop: '7px',
    fontSize: '14px'
  },

  statusBox: {
    background: 'rgba(255,255,255,0.07)',
    color: 'rgba(255,255,255,0.82)',
    borderRadius: '16px',
    padding: '11px 15px',
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    whiteSpace: 'nowrap',
    boxShadow: '0 10px 22px rgba(0,0,0,0.14)',
    border: '1px solid rgba(255,255,255,0.10)',
    fontSize: '13px'
  },

  fullscreenButton: {
    border: '1px solid rgba(103,232,249,0.25)',
    background: 'rgba(6,182,212,0.11)',
    color: '#dffafe',
    borderRadius: '14px',
    padding: '11px 14px',
    cursor: 'pointer',
    fontWeight: '900',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 0 18px rgba(6,182,212,0.18)'
  },

  intelligenceBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '16px',
    padding: '14px',
    borderRadius: '18px',
    background: 'rgba(255,255,255,0.035)',
    border: '1px solid rgba(255,255,255,0.07)'
  },

  controlsGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px'
  },

  tagSelectorWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: 0
  },

  tagSelectorButton: {
    border: '1px solid rgba(103,232,249,0.18)',
    background: 'rgba(6,182,212,0.08)',
    color: '#dffafe',
    borderRadius: '999px',
    padding: '10px 14px',
    cursor: 'pointer',
    fontWeight: '900',
    fontSize: '13px',
    whiteSpace: 'nowrap'
  },

  clearTagsButton: {
    border: '1px solid rgba(255,255,255,0.08)',
    background: 'rgba(255,255,255,0.035)',
    color: 'rgba(255,255,255,0.65)',
    borderRadius: '999px',
    padding: '10px 13px',
    cursor: 'pointer',
    fontWeight: '800',
    fontSize: '13px',
    whiteSpace: 'nowrap'
  },

  tagDropdown: {
    position: 'absolute',
    top: '48px',
    right: 0,
    width: '340px',
    maxHeight: '300px',
    overflowY: 'auto',
    background: '#081f39',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '18px',
    padding: '14px',
    zIndex: 9500,
    boxShadow: '0 18px 40px rgba(0,0,0,0.35)',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },

  tagOption: {
    borderRadius: '999px',
    padding: '8px 11px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '700'
  },

  emptyTags: {
    color: 'rgba(255,255,255,0.55)',
    fontSize: '13px'
  },

  territorialFilters: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))',
    gap: '10px',
    marginBottom: '16px',
    padding: '14px',
    borderRadius: '18px',
    background: 'rgba(255,255,255,0.035)',
    border: '1px solid rgba(255,255,255,0.07)'
  },

  filterSelect: {
    background: '#081f39',
    border: '1px solid rgba(255,255,255,0.16)',
    color: 'white',
    borderRadius: '12px',
    padding: '11px 12px',
    fontWeight: '800',
    fontSize: '12px',
    outline: 'none'
  },

  option: {
    background: '#081f39',
    color: 'white'
  },

  clearTerritoryButton: {
    border: '1px solid rgba(239,68,68,0.35)',
    background: 'rgba(239,68,68,0.12)',
    color: '#fecaca',
    borderRadius: '12px',
    padding: '11px 12px',
    cursor: 'pointer',
    fontWeight: '900',
    fontSize: '12px'
  },

  relationsBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '12px'
  },

  relationsButton: {
    border: '1px solid rgba(103,232,249,0.25)',
    color: '#dffafe',
    borderRadius: '12px',
    padding: '10px 14px',
    cursor: 'pointer',
    fontWeight: '900',
    fontSize: '13px',
    boxShadow: '0 0 18px rgba(6,182,212,0.12)'
  },

  activeEntity: {
    background: 'rgba(6,182,212,0.10)',
    color: '#a5f3fc',
    padding: '10px 13px',
    borderRadius: '13px',
    fontWeight: '800',
    marginBottom: '15px',
    border: '1px solid rgba(6,182,212,0.25)',
    fontSize: '13px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '14px'
  },

  clearConnectionsButton: {
    border: '1px solid rgba(239,68,68,0.35)',
    background: 'rgba(239,68,68,0.12)',
    color: '#fecaca',
    borderRadius: '999px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontWeight: '900',
    fontSize: '12px',
    whiteSpace: 'nowrap'
  },

  toggle: {
    border: 'none',
    borderRadius: '999px',
    padding: '9px 13px',
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    cursor: 'pointer',
    fontWeight: '800',
    fontSize: '13px',
    transition: 'all 0.2s ease'
  },

  toggleIcon: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '15px'
  },

  selectionHint: {
    background: 'rgba(31,169,122,0.12)',
    color: '#d1f5e8',
    padding: '11px 13px',
    borderRadius: '13px',
    fontWeight: '700',
    marginBottom: '15px',
    border: '1px solid rgba(31,169,122,0.25)',
    fontSize: '13px'
  },

  mapShell: {
    height: '460px',
    borderRadius: '20px',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow:
      'inset 0 0 0 1px rgba(255,255,255,0.04), 0 18px 34px rgba(0,0,0,0.22)',
    background: '#071a2f'
  },

  mapShellPdf: {
    height: '560px',
    borderRadius: '16px',
    border: 'none',
    boxShadow: 'none'
  },

  mapShellFullscreen: {
    height: 'calc(100vh - 320px)',
    minHeight: '520px'
  },

  legend: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px',
    marginTop: '14px',
    color: 'rgba(255,255,255,0.64)',
    fontSize: '12px'
  },

  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px'
  },

  dot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    display: 'inline-block'
  },

  lineLegend: {
    width: '24px',
    height: '0',
    borderTop: '2px dashed #ef4444',
    display: 'inline-block'
  },
  lineRel: {
    width: '24px',
    height: '0',
    borderTop: '2px solid',
    display: 'inline-block'
  },
  lineRelDashed: {
    width: '24px',
    height: '0',
    borderTop: '2px dashed',
    display: 'inline-block'
  },
  legendSeparator: {
    width: '1px',
    height: '16px',
    background: 'rgba(255,255,255,0.15)'
  },

  fullscreenHint: {
    marginLeft: 'auto',
    color: 'rgba(165,243,252,0.75)',
    fontWeight: '800'
  },

  coordinates: {
    marginLeft: 'auto',
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: '999px',
    padding: '6px 11px',
    color: 'rgba(255,255,255,0.78)',
    fontWeight: '700',
    fontSize: '12px'
  }
};

export default MapSection;